import { Alert, Severity, theme } from '@arnold/common';
import styled from '@emotion/styled';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.f};
  flex-wrap: wrap;
  width: 100%;
`;

const BannerMessage = styled.span`
  font-weight: 500;
`;

const BannerButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.d};
  flex-shrink: 0;
`;

const BannerButton = styled.button<{ primary?: boolean }>`
  appearance: none;
  border: 1px solid ${(props) => (props.primary ? theme.colors.iconAction.primary : theme.colors.borderMain.default)};
  border-radius: ${theme.borderRadius.m};
  background: ${(props) => (props.primary ? theme.colors.iconAction.primary : theme.background.primary)};
  color: ${(props) => (props.primary ? 'white' : theme.colors.text.secondary)};
  font-size: 12px;
  font-weight: 500;
  padding: ${theme.spacing.c} ${theme.spacing.e};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:hover:not(:disabled) {
    background: ${(props) => (props.primary ? theme.colors.actionPrimary.hover : theme.colors.backgroundBasic.hover)};
    border-color: ${(props) => (props.primary ? theme.colors.actionPrimary.hover : theme.colors.iconAction.primary)};
    color: ${(props) => (props.primary ? 'white' : theme.colors.text.primary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type TranslationIssuesBannerProps = {
  issueCount: number;
  reviewableCount: number;
  hasAiTranslation: boolean;
  onTranslateAll: () => Promise<void>;
  onMarkAllAsOk: () => Promise<void>;
};

export const TranslationIssuesBanner: FC<TranslationIssuesBannerProps> = ({
  issueCount,
  reviewableCount,
  hasAiTranslation,
  onTranslateAll,
  onMarkAllAsOk,
}) => {
  const { t } = useTranslation('topicEditor');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const busy = isTranslating || isMarking;

  const handleTranslateAll = async () => {
    setIsTranslating(true);
    try {
      await onTranslateAll();
    } catch (error) {
      console.error('Error translating all issues:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleMarkAllAsOk = async () => {
    setIsMarking(true);
    try {
      await onMarkAllAsOk();
    } catch (error) {
      console.error('Error marking all as OK:', error);
    } finally {
      setIsMarking(false);
    }
  };

  return (
    <Alert severity={Severity.WARNING} dataCy="translation-issues-banner">
      <BannerContent>
        <BannerMessage>{t('translationIssuesBannerText', { count: issueCount })}</BannerMessage>
        <BannerButtons>
          {hasAiTranslation && (
            <BannerButton primary disabled={busy} onClick={handleTranslateAll} data-cy="translate-all-issues">
              {isTranslating ? '...' : t('translationTranslateAllIssues')}
            </BannerButton>
          )}
          {reviewableCount > 0 && (
            <BannerButton disabled={busy} onClick={handleMarkAllAsOk} data-cy="mark-all-as-ok">
              {isMarking ? '...' : t('translationMarkAllAsOk')}
            </BannerButton>
          )}
        </BannerButtons>
      </BannerContent>
    </Alert>
  );
};
